import type { Medicine } from '../types';

const BANGLA_DIGITS: Record<string, string> = {
  '০': '0',
  '১': '1',
  '২': '2',
  '৩': '3',
  '৪': '4',
  '৫': '5',
  '৬': '6',
  '৭': '7',
  '৮': '8',
  '৯': '9',
};

const LATIN_TO_BANGLA_DIGITS: Record<string, string> = Object.fromEntries(
  Object.entries(BANGLA_DIGITS).map(([bangla, latin]) => [latin, bangla])
);

interface ParsedInstruction {
  rawInstruction: string;
  frequencyPattern?: string;
  frequencyLabel?: string;
  banglaFrequencyLabel?: string;
  timingLabel?: string;
  banglaTimingLabel?: string;
  durationLabel?: string;
  banglaDurationLabel?: string;
  englishInstruction?: string;
  banglaInstruction?: string;
  isNebulization: boolean;
}

function normalizeDigits(value: string): string {
  return value.replace(/[০-৯]/g, (digit) => BANGLA_DIGITS[digit] || digit);
}

function toBanglaDigits(value: string): string {
  return value.replace(/[0-9]/g, (digit) => LATIN_TO_BANGLA_DIGITS[digit] || digit);
}

function compactText(parts: Array<string | undefined>): string {
  return parts
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function sentenceJoin(parts: string[]): string {
  if (parts.length <= 1) return parts[0] || '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

function banglaJoin(parts: string[]): string {
  if (parts.length <= 1) return parts[0] || '';
  if (parts.length === 2) return `${parts[0]} ও ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} ও ${parts[parts.length - 1]}`;
}

function parseFrequency(source: string): Pick<
  ParsedInstruction,
  'frequencyPattern' | 'frequencyLabel' | 'banglaFrequencyLabel'
> {
  const normalized = normalizeDigits(source);
  const match = normalized.match(/([0-9]+)\s*\+\s*([0-9]+)\s*\+\s*([0-9]+)/);
  if (!match) return {};

  const counts = match.slice(1).map((item) => Number(item));
  const englishTimes = ['morning', 'noon', 'night'];
  const banglaTimes = ['সকাল', 'দুপুর', 'রাতে'];
  const englishParts = counts
    .map((count, index) => (count > 0 ? englishTimes[index] : ''))
    .filter(Boolean);
  const banglaParts = counts
    .map((count, index) => (count > 0 ? banglaTimes[index] : ''))
    .filter(Boolean);

  return {
    frequencyPattern: `${counts[0]}+${counts[1]}+${counts[2]}`,
    frequencyLabel: englishParts.length > 0 ? sentenceJoin(englishParts) : undefined,
    banglaFrequencyLabel: banglaParts.length > 0 ? banglaJoin(banglaParts) : undefined,
  };
}

function parseTiming(source: string): Pick<ParsedInstruction, 'timingLabel' | 'banglaTimingLabel'> {
  const normalized = normalizeDigits(source).toLowerCase();

  if (/আধা\s*ঘন্টা\s*(পূর্বে|আগে)|30\s*(min|minute|minutes)\s*before/.test(normalized)) {
    return {
      timingLabel: '30 minutes before food',
      banglaTimingLabel: 'খাবারের ৩০ মিনিট আগে',
    };
  }

  if (/খাবার\s*(আগে|পূর্বে)|before\s*(food|meal|meals)/.test(normalized)) {
    return {
      timingLabel: 'before food',
      banglaTimingLabel: 'খাবারের আগে',
    };
  }

  if (/খাবার\s*(পরে|পর)|after\s*(food|meal|meals)/.test(normalized)) {
    return {
      timingLabel: 'after food',
      banglaTimingLabel: 'খাবারের পরে',
    };
  }

  return {};
}

function parseDuration(source: string): Pick<ParsedInstruction, 'durationLabel' | 'banglaDurationLabel'> {
  const normalized = normalizeDigits(source).toLowerCase();

  if (/চলবে|continue|as advised/.test(normalized)) {
    return {
      durationLabel: 'continue as advised',
      banglaDurationLabel: 'পরামর্শ অনুযায়ী চলবে',
    };
  }

  const duration = normalized.match(/(\d+)\s*(দিন|day|days|d|মাস|month|months|সপ্তাহ|week|weeks)/);
  if (!duration) return {};

  const amount = duration[1];
  const unit = duration[2];
  if (/মাস|month/.test(unit)) {
    return {
      durationLabel: `${amount} month${amount === '1' ? '' : 's'}`,
      banglaDurationLabel: `${toBanglaDigits(amount)} মাস`,
    };
  }

  if (/সপ্তাহ|week/.test(unit)) {
    return {
      durationLabel: `${amount} week${amount === '1' ? '' : 's'}`,
      banglaDurationLabel: `${toBanglaDigits(amount)} সপ্তাহ`,
    };
  }

  return {
    durationLabel: `${amount} day${amount === '1' ? '' : 's'}`,
    banglaDurationLabel: `${toBanglaDigits(amount)} দিন`,
  };
}

function getEnglishUnit(medicine: Medicine): string {
  const form = (medicine.form || '').toLowerCase();
  const dose = (medicine.dosage || '').toLowerCase();

  if (/capsule|cap\b/.test(form) || /capsule|cap\b/.test(dose)) return 'capsule';
  if (/tablet|tab\b/.test(form) || /tablet|tab\b/.test(dose)) return 'tablet';
  if (/injection|inj\b|insulin/.test(form) || /unit/.test(dose)) return 'units/injection';
  if (/syrup|syp/.test(form)) return 'syrup dose';
  if (/drop/.test(form)) return 'drop';
  return 'dose';
}

function getBanglaUnit(medicine: Medicine): string {
  const form = (medicine.form || '').toLowerCase();
  const dose = (medicine.dosage || '').toLowerCase();

  if (/capsule|cap\b/.test(form) || /capsule|cap\b/.test(dose)) return 'ক্যাপসুল';
  if (/tablet|tab\b/.test(form) || /tablet|tab\b/.test(dose)) return 'ট্যাবলেট';
  if (/injection|inj\b|insulin/.test(form) || /unit/.test(dose)) return 'ইনজেকশন/ইউনিট';
  if (/syrup|syp/.test(form)) return 'সিরাপ';
  if (/drop/.test(form)) return 'ড্রপ';
  return 'ডোজ';
}

function inferDose(medicine: Medicine, parsed: ParsedInstruction): { english: string; bangla: string } {
  const explicitDose = medicine.dosage || medicine.strength;
  if (explicitDose) {
    return { english: explicitDose, bangla: formatBanglaDose(explicitDose) };
  }

  if (parsed.frequencyPattern) {
    return {
      english: `1 ${getEnglishUnit(medicine)}`,
      bangla: `১টি ${getBanglaUnit(medicine)}`,
    };
  }

  return {
    english: 'the prescribed dose',
    bangla: 'প্রেসক্রিপশনে লেখা ডোজ',
  };
}

function formatBanglaDose(value: string): string {
  return toBanglaDigits(value)
    .replace(/\btablet(s)?\b/gi, 'ট্যাবলেট')
    .replace(/\btab\b/gi, 'ট্যাবলেট')
    .replace(/\bcapsule(s)?\b/gi, 'ক্যাপসুল')
    .replace(/\bcap\b/gi, 'ক্যাপসুল')
    .replace(/\bunit(s)?\b/gi, 'ইউনিট')
    .replace(/\binjection\b/gi, 'ইনজেকশন');
}

function isNebulizationInstruction(source: string, medicine: Medicine): boolean {
  return /neb|nebul|nebulization|নেবুলাইজ/i.test(
    compactText([source, medicine.name, medicine.form, medicine.notes])
  );
}

export function parsePrescriptionInstruction(medicine: Medicine): ParsedInstruction {
  const originalInstruction = compactText([
    medicine.rawInstruction,
    medicine.instruction,
    medicine.notes,
  ]);
  const source = compactText([
    medicine.rawInstruction,
    medicine.instruction,
    medicine.frequency,
    medicine.timing,
    medicine.duration,
    medicine.notes,
  ]);
  const parsed: ParsedInstruction = {
    rawInstruction: originalInstruction || source,
    ...parseFrequency(source || compactText([medicine.dosage, medicine.strength])),
    ...parseTiming(source || compactText([medicine.dosage, medicine.strength])),
    ...parseDuration(source || compactText([medicine.dosage, medicine.strength])),
    isNebulization: isNebulizationInstruction(source, medicine),
  };
  const dose = inferDose(medicine, parsed);

  if (parsed.isNebulization) {
    const timing = parsed.timingLabel ? ` ${parsed.timingLabel}` : '';
    const duration = parsed.durationLabel ? ` for ${parsed.durationLabel}` : '';
    parsed.englishInstruction = `Use nebulization as prescribed${timing}${duration}.`;
    parsed.banglaInstruction = `নেবুলাইজেশন প্রেসক্রিপশন অনুযায়ী ব্যবহার করুন${parsed.banglaTimingLabel ? `, ${parsed.banglaTimingLabel}` : ''}${parsed.banglaDurationLabel ? `, ${parsed.banglaDurationLabel}` : ''}।`;
    return parsed;
  }

  const action = getEnglishUnit(medicine) === 'units/injection' ? 'Use' : 'Take';
  const timing = parsed.timingLabel ? ` ${parsed.timingLabel}` : '';
  const frequency = parsed.frequencyLabel ? `, ${parsed.frequencyLabel}` : '';
  const duration = parsed.durationLabel
    ? parsed.durationLabel === 'continue as advised'
      ? ', continue as advised'
      : `, for ${parsed.durationLabel}`
    : '';

  parsed.englishInstruction = `${action} ${dose.english}${timing}${frequency}${duration}.`;

  const banglaTiming = parsed.banglaTimingLabel ? `${parsed.banglaTimingLabel} ` : '';
  const banglaFrequency = parsed.banglaFrequencyLabel ? ` ${parsed.banglaFrequencyLabel}` : '';
  const banglaBase = `${banglaTiming}${banglaFrequency ? `${banglaFrequency} ` : ''}${dose.bangla}`.trim();
  const banglaDuration =
    parsed.durationLabel === 'continue as advised'
      ? ' পরামর্শ অনুযায়ী চালিয়ে যাবেন'
      : parsed.banglaDurationLabel
        ? ` ${parsed.banglaDurationLabel} খাবেন`
        : ' খাবেন';
  parsed.banglaInstruction = `${banglaBase}${banglaDuration}।`
    .replace(/\s+/g, ' ')
    .trim();

  return parsed;
}

export function enhanceMedicineInstructions(medicine: Medicine): Medicine {
  const parsed = parsePrescriptionInstruction(medicine);

  return {
    ...medicine,
    rawInstruction: parsed.rawInstruction || medicine.rawInstruction,
    instruction: medicine.instruction || parsed.englishInstruction,
    frequency: medicine.frequency || parsed.frequencyLabel || '',
    timing: medicine.timing || parsed.timingLabel || '',
    duration: medicine.duration || parsed.durationLabel || '',
    frequencyLabel: medicine.frequencyLabel || parsed.frequencyLabel,
    banglaFrequencyLabel: medicine.banglaFrequencyLabel || parsed.banglaFrequencyLabel,
    timingLabel: medicine.timingLabel || parsed.timingLabel,
    banglaTimingLabel: medicine.banglaTimingLabel || parsed.banglaTimingLabel,
    durationLabel: medicine.durationLabel || parsed.durationLabel,
    banglaDurationLabel: medicine.banglaDurationLabel || parsed.banglaDurationLabel,
    englishInstruction: medicine.englishInstruction || parsed.englishInstruction,
    banglaInstruction: medicine.banglaInstruction || parsed.banglaInstruction,
    isNebulization: parsed.isNebulization,
  };
}

export function enhanceMedicinesWithInstructions(medicines: Medicine[]): Medicine[] {
  return medicines.map(enhanceMedicineInstructions);
}

export function hasUsableInstruction(medicine: Medicine): boolean {
  return Boolean(
    medicine.englishInstruction ||
      medicine.instruction ||
      medicine.frequency ||
      medicine.timing ||
      medicine.duration ||
      medicine.rawInstruction
  );
}
